import React, { Suspense, lazy, useCallback, useRef, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { format, startOfMonth, subDays } from "date-fns";
import Spinner from "../../../../components/common/Spinner";
import { CalendarIcon, ChevronDownIcon, ClearIcon } from "./icons";
import { IDateRange } from "./constants";
import { useDismiss } from "./useDismiss";
import styles from "./styles.module.scss";

const RangeCalendar = lazy(() => import("./RangeCalendar"));

const DISPLAY_FORMAT = "yyyy/MM/dd";

interface Props {
  value: IDateRange;
  onChange: (value: IDateRange) => void;
}

/** Creation-date range: a calendar popover that applies once both ends are picked. */
const DateRangeFilter: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  // The calendar's own selection while the popover is open; committed on the second pick.
  const [draft, setDraft] = useState<IDateRange>(value);
  const [pickingEnd, setPickingEnd] = useState(false);

  const close = useCallback(() => setOpen(false), []);
  useDismiss(ref, open, close);

  const hasValue = !!value.from && !!value.to;
  const today = new Date();

  const toggle = () => {
    if (!open) {
      setDraft(value);
      setPickingEnd(false);
    }
    setOpen((prev) => !prev);
  };

  const apply = (range: IDateRange) => {
    onChange(range);
    close();
  };

  const onCalendarChange = (next: IDateRange) => {
    setDraft(next);
    if (pickingEnd) {
      apply(next);
    } else {
      setPickingEnd(true);
    }
  };

  const presets = [
    { label: t("lbl_contracts_range_last_7"), from: subDays(today, 6) },
    { label: t("lbl_contracts_range_last_30"), from: subDays(today, 29) },
    { label: t("lbl_contracts_range_this_month"), from: startOfMonth(today) },
  ];

  return (
    <div ref={ref} className={styles.field}>
      <div
        className={classNames(styles.control, {
          [styles.controlActive]: hasValue,
          [styles.controlOpen]: open,
        })}>
        <button
          type="button"
          className={styles.trigger}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={toggle}>
          <span className={styles.controlIcon}>
            <CalendarIcon />
          </span>
          {hasValue && value.from && value.to ? (
            <span className={styles.controlText}>
              <bdi>{format(value.from, DISPLAY_FORMAT)}</bdi>
              <span className={styles.rangeDash}>–</span>
              <bdi>{format(value.to, DISPLAY_FORMAT)}</bdi>
            </span>
          ) : (
            <span
              className={classNames(styles.controlText, styles.placeholder)}>
              {t("lbl_contracts_filter_created_at")}
            </span>
          )}
          {!hasValue && <ChevronDownIcon className={styles.chevron} />}
        </button>
        {hasValue && (
          <button
            type="button"
            className={styles.clearButton}
            aria-label={t("lbl_org_act_clear") as string}
            onClick={() => onChange({ from: null, to: null })}>
            <ClearIcon />
          </button>
        )}
      </div>

      {open && (
        <div
          role="dialog"
          className={classNames(styles.popover, styles.calendar)}>
          <div className={styles.presets}>
            {presets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                className={styles.presetChip}
                onClick={() => apply({ from: preset.from, to: today })}>
                {preset.label}
              </button>
            ))}
          </div>
          <Suspense
            fallback={
              <div className={styles.calendarLoading}>
                <Spinner />
              </div>
            }>
            <RangeCalendar
              value={draft}
              maxDate={today}
              onChange={onCalendarChange}
            />
          </Suspense>
          <p className={styles.calendarHint}>
            {pickingEnd
              ? t("lbl_contracts_range_pick_end")
              : t("lbl_contracts_range_pick_start")}
          </p>
        </div>
      )}
    </div>
  );
};

export default DateRangeFilter;
